// Small pieces every Agences screen shares, for MyGaz and for the agency:
// the neutral initials, the status pills, stars and the network marks.
import { useState } from 'react';
import { S } from '../../lib/strings';
import { initials } from '../../lib/format';
import { accountState, rating, riderStatus } from '../../lib/agences';
import type { Account, AgRider, Provider } from '../../lib/types';

export function Av({ name, size = 36 }: { name: string; size?: number }) {
  return <span className="ag-av" style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }} aria-hidden="true">{initials(name)}</span>;
}

export function RiderPill({ r }: { r: Pick<AgRider, 'state' | 'busy' | 'live' | 'seen_at'> }) {
  const s = riderStatus(r);
  return <span className={'st ' + s.tone}>{S.ag.rs[s.key]}</span>;
}

export function AccountPill({ a }: { a: Pick<Account, 'balance' | 'paused' | 'days_to_zero'> }) {
  const s = accountState(a);
  return <span className={'st ' + s.tone}>{S.ag.acc[s.key]}</span>;
}

export function Star({ s = 13 }: { s?: number }) {
  return (
    <svg className="ag-star" width={s} height={s} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />
    </svg>
  );
}

export function Stars({ value, count }: { value: number | null; count?: number }) {
  if (value == null) return <span className="muted">{S.ag.tbl.notRated}</span>;
  return <span className="ag-rt"><Star /><b>{rating(value)}</b>{count != null && <span className="muted">{count}</span>}</span>;
}

/** The network's own mark: MTN on its yellow, Orange on its orange (vault rule on real logos). */
export function Net({ p, s = 26 }: { p: Provider | string; s?: number }) {
  const mtn = p === 'MTN_MOMO_CMR' || p === 'MTN MoMo';
  return (
    <span className={'ag-net ' + (mtn ? 'mtn' : 'org')} style={{ width: s, height: s }} title={mtn ? 'MTN MoMo' : 'Orange Money'}>
      <img src={mtn ? './networks/mtn.png' : './networks/orange.svg'} alt={mtn ? 'MTN' : 'Orange'} />
    </span>
  );
}

/** « Ce que X ne voit jamais ici », on every agency screen. */
export function Privacy({ agency }: { agency: string }) {
  return (
    <div className="ag-priv">
      <svg className="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
      <div><b>{S.ag.priv(agency)}</b> {S.ag.privBody}</div>
    </div>
  );
}

/** A column whose top corners are rounded and whose foot stands square on the baseline. */
function colPath(x: number, w: number, base: number, h: number, r = 4) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${base} V${base - h + rr} Q${x},${base - h} ${x + rr},${base - h} H${x + w - rr} Q${x + w},${base - h} ${x + w},${base - h + rr} V${base} Z`;
}

/**
 * Columns for a short series (a week, the hours of a day): capped at 24 px, the
 * highlighted one in blue, the others a light step of the same blue; one value
 * written (the highlighted one), the rest on hover.
 */
export function WeekBars({ vals, labels, w = 420, h = 96, today = true, mark, fmt = String }: {
  vals: number[]; labels: string[]; w?: number; h?: number; today?: boolean; mark?: number; fmt?(v: number): string;
}) {
  const [hov, setHov] = useState<number | null>(null);
  const hi = mark ?? (today ? vals.length - 1 : vals.indexOf(Math.max(...vals)));
  const mx = Math.max(1, ...vals), slot = w / vals.length, bw = Math.min(24, slot * 0.62), base = h + 18;
  return (
    <svg className="ag-bars" viewBox={`0 0 ${w} ${h + 40}`} width="100%" role="img" aria-label={vals.map((v, i) => `${labels[i]} ${fmt(v)}`).join(', ')} onPointerLeave={() => setHov(null)}>
      <line x1="0" x2={w} y1={base} y2={base} className="base" />
      {vals.map((v, i) => {
        const bh = v > 0 ? Math.max(3, (v / mx) * h) : 0, x = i * slot + (slot - bw) / 2, show = i === hov || (hov == null && i === hi && v > 0);
        return (
          <g key={i} onPointerEnter={() => setHov(i)}>
            <rect x={i * slot} y="0" width={slot} height={base + 2} fill="transparent" />
            {bh > 0 && <path d={colPath(x, bw, base, bh)} className={i === hi ? 'on' : i === hov ? 'hov' : ''} />}
            {show && <text x={x + bw / 2} y={base - bh - 7} textAnchor="middle" className="v">{fmt(v)}</text>}
            <text x={x + bw / 2} y={base + 16} textAnchor="middle" className={'l' + (i === hi ? ' on' : '')}>{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Seven small columns in a table cell: today in blue, the days before a light blue. */
export function MiniBars({ vals, labels }: { vals: number[]; labels?: string[] }) {
  const bw = 7, gap = 3, h = 24, mx = Math.max(1, ...vals);
  return (
    <svg className="ag-mini" width={vals.length * (bw + gap) - gap} height={h} viewBox={`0 0 ${vals.length * (bw + gap) - gap} ${h}`} role="img"
      aria-label={vals.map((v, i) => `${labels?.[i] ?? ''} ${v}`.trim()).join(', ')}>
      {vals.map((v, i) => {
        const bh = v > 0 ? Math.max(3, (v / mx) * (h - 2)) : 1.5;
        return (
          <path key={i} d={v > 0 ? colPath(i * (bw + gap), bw, h, bh, 2) : `M${i * (bw + gap)},${h - 1.5} h${bw} v1.5 h${-bw} Z`} className={v <= 0 ? 'zero' : i === vals.length - 1 ? 'on' : ''}>
            <title>{`${labels?.[i] ?? ''} · ${v}`}</title>
          </path>
        );
      })}
    </svg>
  );
}

/** The last seven days' short names, oldest first, today last. */
export function last7(now = Date.now()) {
  return Array.from({ length: 7 }, (_, i) => new Date(now - (6 - i) * 86400000).toLocaleDateString('fr-FR', { weekday: 'short', timeZone: 'Africa/Douala' }));
}

/** A sentence with each « MTN MoMo » or « Orange Money » carrying its network's mark. */
export function NetText({ text }: { text: string }) {
  const parts = text.split(/(MTN MoMo|Orange Money)/);
  return <>{parts.map((p, i) => (p === 'MTN MoMo' || p === 'Orange Money' ? <span key={i} className="ag-nw"><Net p={p} s={16} />{p}</span> : p))}</>;
}
